// BUILD-046 Owner-requested visual edit (after candidate fb6aee68): a date field with quick buttons - the native date input (its calendar picker stays)
// followed on the same row by light-blue Today / Tomorrow / Next week pill buttons and a blue Clear link.
//
// Generic on purpose: the label, the value, the change handler and the id prefix are props, so another date field can use the same buttons later. It holds
// no state of its own (the host owns the `YYYY-MM-DD` string; an empty string means no date). The date arithmetic is in quickDateModel.ts (pure and tested);
// "today" is read at the moment of the click, as the Phoenix calendar day, so a dialog left open past midnight still gives the right day.
//
// The input keeps the id given as `idPrefix` (a host's existing label, tests and focus code keep working); the buttons are `${idPrefix}-quick-<choice>`.
// The buttons are type="button" (they never submit a form), each has an accessible name like "Set Task due date to tomorrow" (Clear: "Clear Task due
// date"), they are reachable in the Tab order right after the input, and they show a focus ring. A quick button changes the value only; it is not tied to
// the Edit form.
import { phoenixToday } from './dateLabels';
import { describeQuickDateButtons, quickDateValue } from './quickDateModel';

export interface QuickDateFieldProps {
  /** The id of the date input; also the prefix of the quick button ids. */
  idPrefix: string;
  /** The field's name, for example "Task due date". The visible label is this text, plus " (optional)" when `optional` is set. */
  label: string;
  optional?: boolean;
  /** The current value: `YYYY-MM-DD`, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Ids of elements that describe the field (for example an inline problem), joined by spaces. */
  describedBy?: string;
  /** The clock, replaceable so a check can pin the day; the default is the real time. */
  now?: () => Date;
  /**
   * `inline` (default, as before): the input takes about 45% of the row and the pill buttons follow on the same row. `stacked` (Assign task, cycle C): the input
   * fills its column and Today, Tomorrow, Next week and Clear sit beneath it as small text buttons. Same buttons, same names, same behavior.
   */
  layout?: 'inline' | 'stacked';
}

// The date input takes about 45% of the row from 640px up (the full width below that); the light-blue pill buttons (Today, Tomorrow, Next week) and the blue
// Clear link follow on the SAME row and wrap to the next line when there is no room.
const INPUT = 'w-full sm:w-[45%] px-3 py-2 border rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE] border-gray-300 disabled:opacity-60';
const PILL_BUTTON =
  'rounded-full border border-sky-100 bg-sky-50 px-3 py-1.5 text-xs font-medium text-[#1f6a99] hover:bg-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50 disabled:hover:bg-sky-50';
const CLEAR_LINK =
  'rounded px-1.5 py-1 text-xs font-medium text-[#1f6a99] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50 disabled:hover:no-underline';
// Stacked layout: the input fills its column; the four choices are small text buttons beneath it (a 24px-high hit area each).
const STACKED_INPUT = 'w-full px-3 py-2 border rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE] border-gray-300 disabled:opacity-60';
const TEXT_BUTTON =
  'inline-flex min-h-[1.5rem] items-center rounded px-1 text-xs font-medium text-[#1f6a99] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50 disabled:hover:no-underline';

export function QuickDateField({ idPrefix, label, optional = false, value, onChange, disabled = false, describedBy, now, layout = 'inline' }: QuickDateFieldProps) {
  const buttons = describeQuickDateButtons(label);
  const stacked = layout === 'stacked';
  return (
    <div>
      <label htmlFor={idPrefix} className="mb-1 block text-xs font-medium text-gray-700">
        {optional ? `${label} (optional)` : label}
      </label>
      <div className={stacked ? 'flex flex-col gap-0.5' : 'flex flex-wrap items-center gap-x-2 gap-y-1.5'} data-testid={`${idPrefix}-row`}>
        <input id={idPrefix} type="date" value={value} disabled={disabled} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} className={stacked ? STACKED_INPUT : INPUT} />
        <div role="group" aria-label={`Quick choices for ${label}`} className={stacked ? 'flex flex-wrap items-center gap-x-2 gap-y-0' : 'flex flex-wrap items-center gap-1.5'} data-testid={`${idPrefix}-quick`}>
          {buttons.map((button) => (
            <button
              key={button.choice}
              id={`${idPrefix}-quick-${button.choice}`}
              type="button"
              disabled={disabled}
              aria-label={button.name}
              title={button.name}
              onClick={() => {
                const next = quickDateValue(button.choice, phoenixToday(now === undefined ? new Date() : now()));
                if (next !== null) onChange(next);
              }}
              className={stacked ? TEXT_BUTTON : button.choice === 'clear' ? CLEAR_LINK : PILL_BUTTON}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
